import { useState, useEffect } from "react"
import { Plus, Search, Filter, Car, Eye, Stethoscope, FileText } from "lucide-react"
import { Link, useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { supabase } from "@/integrations/supabase/client"
import { toast } from "sonner"
import { PageHeader } from "@/components/PageHeader"
import { useIsMobile } from "@/hooks/use-mobile"
import { useTipoOperacao } from "@/hooks/useTipoOperacao"
import VehiclePhotoGallery from "@/components/VehiclePhotoGallery"
import { PHOTO_BUCKET, createSignedFileUrl } from "@/lib/vehicleStorage"
import { getPublicErrorMessage } from "@/lib/errors"
import { getOficinaAtendimentoStatus } from "@/lib/oficinaAtendimento"
import { ESTOQUE_STATUS_LABELS, getLojaComercialStatus, type EstoqueStatus } from "@/lib/lojaComercial"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

interface Veiculo {
  id: string
  placa: string
  marca: string
  modelo: string
  ano: number
  cor?: string | null
  combustivel?: string | null
  cambio?: string | null
  quilometragem?: number | null
  motor?: string | null
  portas?: number | null
  observacoes?: string | null
  foto_principal?: string | null
  created_at: string
  estoque_status?: string | null
  fotos?: string[] // Array com todas as fotos do veículo (opcional)
  avaliacoes?: Array<{
    id: string
    status: string
    objetivo_comercial?: string | null
    share_token: string | null
    cliente_nome: string | null
    cliente_telefone: string | null
    cliente_assinado_em: string | null
    atendimento_finalizado_em: string | null
    created_at: string
  }>
  propostas?: Array<{
    id: string
    avaliacao_id: string
    modalidade: string | null
    status: string | null
    share_token: string | null
    cliente_assinado_em: string | null
    valor_ofertado: number | null
    valor_fipe: number | null
    custo_reparos: number | null
  }>
  diagnosticos?: Array<{
    id: string
    avaliacao_id: string | null
    status: string
    share_token: string | null
    cliente_ciente_em: string | null
    cliente_autorizou_em: string | null
  }>
}

const Veiculos = () => {
  const navigate = useNavigate()
  const vertical = useTipoOperacao()
  const [searchTerm, setSearchTerm] = useState("")
  const [veiculos, setVeiculos] = useState<Veiculo[]>([])
  const [loading, setLoading] = useState(true)
  const [diagnosticoAvulsoVeiculo, setDiagnosticoAvulsoVeiculo] = useState<Veiculo | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    fetchVeiculos()
  }, [])

  const fetchVeiculos = async () => {
    try {
      // Primeiro, buscar todos os veículos
      const { data: veiculosData, error: veiculosError } = await supabase
        .from('veiculos')
        .select('*')
        .order('created_at', { ascending: false })

      if (veiculosError) {
        console.error("Erro ao carregar veículos:", veiculosError)
        toast.error(getPublicErrorMessage(veiculosError.message, "Não foi possível carregar os veículos agora."))
        return
      }

      if (!veiculosData) {
        setVeiculos([])
        return
      }

      const veiculoIds = veiculosData.map((veiculo) => veiculo.id)
      const avaliacoesPorVeiculo: Record<string, NonNullable<Veiculo["avaliacoes"]>> = {}
      const diagnosticosPorVeiculo: Record<string, NonNullable<Veiculo["diagnosticos"]>> = {}

      if (veiculoIds.length > 0) {
        const { data: avaliacoesData } = await supabase
          .from("avaliacoes")
          .select("id, veiculo_id, status, objetivo_comercial, share_token, cliente_nome, cliente_telefone, cliente_assinado_em, atendimento_finalizado_em, created_at")
          .in("veiculo_id", veiculoIds)
          .order("created_at", { ascending: false })

        avaliacoesData?.forEach((avaliacao) => {
          avaliacoesPorVeiculo[avaliacao.veiculo_id] ??= []
          avaliacoesPorVeiculo[avaliacao.veiculo_id].push(avaliacao)
        })
      }

      if (vertical.tipo === "oficina" && veiculoIds.length > 0) {
        const { data: diagnosticosData } = await supabase
          .from("diagnosticos")
          .select("id, veiculo_id, avaliacao_id, status, share_token, cliente_ciente_em, cliente_autorizou_em")
          .in("veiculo_id", veiculoIds)
          .order("created_at", { ascending: false })

        diagnosticosData?.forEach((diagnostico) => {
          diagnosticosPorVeiculo[diagnostico.veiculo_id] ??= []
          diagnosticosPorVeiculo[diagnostico.veiculo_id].push(diagnostico)
        })
      }

      const propostasPorVeiculo: Record<string, NonNullable<Veiculo["propostas"]>> = {}
      if (vertical.tipo === "loja") {
        const avaliacaoIds = Object.values(avaliacoesPorVeiculo).flat().map((avaliacao) => avaliacao.id)
        if (avaliacaoIds.length > 0) {
          const { data: propostasData } = await supabase
            .from("propostas")
            .select("id, avaliacao_id, modalidade, status, share_token, cliente_assinado_em, valor_ofertado, valor_fipe, custo_reparos")
            .in("avaliacao_id", avaliacaoIds)
            .order("updated_at", { ascending: false })

          propostasData?.forEach((proposta) => {
            const veiculoId = Object.entries(avaliacoesPorVeiculo).find(([, avaliacoes]) =>
              avaliacoes.some((avaliacao) => avaliacao.id === proposta.avaliacao_id)
            )?.[0]
            if (!veiculoId) return
            propostasPorVeiculo[veiculoId] ??= []
            propostasPorVeiculo[veiculoId].push(proposta)
          })
        }
      }

      // Para cada veículo, buscar suas fotos
      const veiculosComFotos = await Promise.all(
        veiculosData.map(async (veiculo) => {
          // Buscar avaliações de cadastro (status: pendente) para o veículo
          const { data: avaliacoesCadastro } = await supabase
            .from('avaliacoes')
            .select('id')
            .eq('veiculo_id', veiculo.id)
            .eq('status', 'pendente')

          let fotos: string[] = []

          if (avaliacoesCadastro && avaliacoesCadastro.length > 0) {
            const avaliacaoIds = avaliacoesCadastro.map(a => a.id)
            
            // Buscar fotos vinculadas a essas avaliações
            const { data: fotosData } = await supabase
              .from('fotos_veiculo')
              .select('url')
              .in('avaliacao_id', avaliacaoIds)
              .order('created_at', { ascending: false })

            fotos = fotosData
              ? (await Promise.all(fotosData.map(async (f) => (await createSignedFileUrl(PHOTO_BUCKET, f.url)) ?? f.url)))
              : []
          }

          // Incluir a foto principal se existir
          if (veiculo.foto_principal) {
            const fotoPrincipalUrl = await createSignedFileUrl(PHOTO_BUCKET, veiculo.foto_principal)
            if (fotoPrincipalUrl) {
              fotos = [fotoPrincipalUrl, ...fotos.filter(url => url !== fotoPrincipalUrl)]
            }
          }

          return {
            ...veiculo,
            fotos,
            avaliacoes: avaliacoesPorVeiculo[veiculo.id] ?? [],
            propostas: propostasPorVeiculo[veiculo.id] ?? [],
            diagnosticos: diagnosticosPorVeiculo[veiculo.id] ?? [],
          }
        })
      )

      setVeiculos(veiculosComFotos)
    } catch (error) {
      toast.error("Erro inesperado ao carregar veículos")
      console.error("Erro:", error)
    } finally {
      setLoading(false)
    }
  }

  const filteredVeiculos = veiculos.filter(veiculo =>
    veiculo.placa.toLowerCase().includes(searchTerm.toLowerCase()) ||
    veiculo.modelo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    veiculo.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
    veiculo.ano.toString().includes(searchTerm)
  )

  const vehicleCards = [
    {
      label: "Base total",
      value: veiculos.length,
      note: "Veículos cadastrados",
    },
    {
      label: "Com fotos",
      value: veiculos.filter((veiculo) => (veiculo.fotos?.length || 0) > 0).length,
      note: "Prontos para inspeção visual",
    },
    {
      label: "Automáticos",
      value: veiculos.filter((veiculo) => (veiculo.cambio || "").toLowerCase().includes("auto")).length,
      note: "Perfil da frota atual",
    },
    {
      label: "Na visão atual",
      value: filteredVeiculos.length,
      note: "Resultado da busca aplicada",
    },
  ]

  const getFluxoOficina = (veiculo: Veiculo) => {
    const avaliacao = veiculo.avaliacoes?.find((item) => ["pendente", "ativa", "em_andamento"].includes(item.status))
      ?? veiculo.avaliacoes?.find((item) => item.status === "concluida")
      ?? veiculo.avaliacoes?.[0]
      ?? null
    const diagnosticos = veiculo.diagnosticos ?? []
    const atendimento = getOficinaAtendimentoStatus(avaliacao, diagnosticos)
    // Diagnóstico vinculado à avaliação atual
    const diagnostico = avaliacao
      ? diagnosticos.find(d => d.avaliacao_id ? d.avaliacao_id === avaliacao.id : true) ?? null
      : null
    return { avaliacao, atendimento, diagnostico }
  }

  const getPrimaryAction = (veiculo: Veiculo) => {
    if (vertical.tipo !== "oficina") {
      const avaliacao = veiculo.avaliacoes?.find((item) => ["pendente", "ativa", "em_andamento"].includes(item.status))
        ?? veiculo.avaliacoes?.find((item) => item.status === "concluida")
        ?? veiculo.avaliacoes?.[0]
        ?? null
      const proposta = avaliacao ? veiculo.propostas?.find((item) => item.avaliacao_id === avaliacao.id) ?? null : null
      const comercial = getLojaComercialStatus(avaliacao, proposta, veiculo.estoque_status)
      if (!avaliacao) return { label: "Escolher objetivo", icon: FileText, href: `/veiculo/${veiculo.id}` }
      if (comercial.status === "avaliacao_iniciada") return { label: "Continuar avaliação", icon: FileText, href: `/avaliacao/${avaliacao.id}` }
      if (["avaliacao_concluida", "proposta_preparada", "proposta_enviada", "proposta_aceita"].includes(comercial.status)) return { label: comercial.proximaAcao, icon: FileText, href: `/veiculo/${veiculo.id}` }
      return { label: "Ver comercial", icon: Eye, href: `/veiculo/${veiculo.id}` }
    }

    const { avaliacao, atendimento, diagnostico } = getFluxoOficina(veiculo)
    const diagnosticoHref = diagnostico ? `/diagnostico/${diagnostico.id}` : null
    if (!avaliacao) return { label: "Iniciar entrada", icon: FileText, href: `/veiculo/${veiculo.id}` }
    if (atendimento.status === "entrada_criada") return { label: "Continuar entrada", icon: FileText, href: `/avaliacao/${avaliacao.id}` }
    if (atendimento.status === "entrada_finalizada") return { label: "Emitir termo", icon: FileText, href: `/proposta/${avaliacao.id}` }
    if (atendimento.status === "termo_emitido") return { label: "Enviar termo", icon: FileText, href: `/proposta/${avaliacao.id}` }
    if (atendimento.status === "cliente_ciente_termo") return { label: "Finalizar atendimento", icon: FileText, href: `/veiculo/${veiculo.id}` }
    if (atendimento.status === "diagnostico_criado") return { label: "Enviar diagnóstico", icon: Stethoscope, href: diagnosticoHref ?? `/veiculo/${veiculo.id}` }
    if (atendimento.status === "diagnostico_enviado") return { label: "Aguardando cliente", icon: Stethoscope, href: diagnosticoHref ?? `/veiculo/${veiculo.id}` }
    if (atendimento.status === "cliente_ciente_diagnostico") return { label: "Ver diagnóstico", icon: Stethoscope, href: diagnosticoHref ?? `/veiculo/${veiculo.id}` }
    if (atendimento.status === "cliente_autorizou") return { label: "Finalizar atendimento", icon: Stethoscope, href: diagnosticoHref ?? `/veiculo/${veiculo.id}` }
    if (atendimento.status === "finalizado") return { label: "Ver atendimento", icon: Eye, href: `/veiculo/${veiculo.id}` }
    return { label: atendimento.proximaAcao, icon: Stethoscope, href: `/veiculo/${veiculo.id}` }
  }

  const getFluxoLoja = (veiculo: Veiculo) => {
    const avaliacao = veiculo.avaliacoes?.find((item) => ["pendente", "ativa", "em_andamento"].includes(item.status))
      ?? veiculo.avaliacoes?.find((item) => item.status === "concluida")
      ?? veiculo.avaliacoes?.[0]
      ?? null
    const proposta = avaliacao ? veiculo.propostas?.find((item) => item.avaliacao_id === avaliacao.id) ?? null : null
    return getLojaComercialStatus(avaliacao, proposta, veiculo.estoque_status)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Veículos"
        description={vertical.veiculos.descricaoListagem}
      >
        <Button asChild>
          <Link to="/novo-veiculo">
            <Plus className="mr-2 h-4 w-4" />
            Novo Veículo
          </Link>
        </Button>
      </PageHeader>

      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-2 xl:grid-cols-4">
        {vehicleCards.map((item) => (
          <Card key={item.label}>
            <CardContent className="flex min-h-[104px] flex-col justify-between p-3.5 sm:min-h-[132px] sm:p-5">
              <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground sm:text-[11px] sm:tracking-[0.18em]">{item.label}</p>
              <p className="font-data mt-2 text-[2rem] font-semibold leading-none text-foreground sm:mt-3 sm:text-3xl">{item.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{item.note}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Search Bar */}
      <Card>
        <CardContent className="p-3.5 sm:p-4 md:p-5 md:pt-5">
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground sm:top-3.5" />
              <Input
                placeholder="Buscar por placa, modelo ou ano..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="h-10 pl-10 sm:h-11"
              />
            </div>
            {isMobile ? (
              <Button variant="outline" className="w-full justify-start">
                <Filter className="mr-2 h-4 w-4" />
                Filtrar
              </Button>
            ) : (
              <Button variant="outline" size="icon">
                <Filter className="h-4 w-4" />
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Vehicles Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Veículos</CardTitle>
          <CardDescription>
            {filteredVeiculos.length} veículo{filteredVeiculos.length !== 1 ? 's' : ''} encontrado{filteredVeiculos.length !== 1 ? 's' : ''}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredVeiculos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-6 mb-4">
                <Car className="h-12 w-12 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                {searchTerm ? "Nenhum veículo encontrado" : "Nenhum veículo cadastrado"}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-sm">
                {searchTerm 
                  ? "Tente ajustar os termos de busca ou cadastre um novo veículo"
                  : "Comece cadastrando um veículo para iniciar as avaliações"
                }
              </p>
              <Button asChild size={isMobile ? "sm" : "default"}>
                <Link to="/novo-veiculo">
                  <Plus className="mr-2 h-4 w-4" />
                  Cadastrar Primeiro Veículo
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:gap-6 lg:grid-cols-3">
              {filteredVeiculos.map((veiculo) => {
                const primaryAction = getPrimaryAction(veiculo)
                const PrimaryIcon = primaryAction.icon
                const fluxo = vertical.tipo === "oficina" ? getFluxoOficina(veiculo) : null
                const fluxoLoja = vertical.tipo === "loja" ? getFluxoLoja(veiculo) : null
                const podeDiagnosticoVinculado = Boolean(fluxo?.avaliacao)

                return (
                  <Card key={veiculo.id} className="group overflow-hidden rounded-[1.3rem] hover:shadow-lg transition-all duration-300">
                    <CardContent className="p-0">
                      <VehiclePhotoGallery
                        photos={veiculo.fotos || []}
                        alt={`Foto do veículo ${veiculo.placa}`}
                        className="group-hover:scale-[1.02] transition-transform duration-300"
                      />

                      <div className="space-y-2.5 p-3 sm:space-y-3 sm:p-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-base font-semibold sm:text-lg">{veiculo.placa}</h3>
                          <Badge variant="secondary" className="text-[10px] sm:text-xs">{veiculo.ano}</Badge>
                        </div>

                        <p className="text-xs text-muted-foreground sm:text-sm">
                          {veiculo.marca} {veiculo.modelo}
                          {veiculo.cor && ` • ${veiculo.cor}`}
                        </p>

                        {vertical.tipo === "oficina" && fluxo && (
                          <div className="rounded-lg border bg-muted/25 px-2.5 py-2">
                            <p className="text-xs font-medium text-foreground">{fluxo.atendimento.label}</p>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{fluxo.atendimento.proximaAcao}</p>
                            {(fluxo.avaliacao?.cliente_nome || fluxo.avaliacao?.cliente_telefone) && (
                              <p className="mt-1 truncate text-[11px] text-muted-foreground">
                                {fluxo.avaliacao.cliente_nome || "Cliente"}{fluxo.avaliacao.cliente_telefone ? ` · ${fluxo.avaliacao.cliente_telefone}` : ""}
                              </p>
                            )}
                          </div>
                        )}
                        {vertical.tipo === "loja" && fluxoLoja && (
                          <div className="rounded-lg border bg-muted/25 px-2.5 py-2">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-xs font-medium text-foreground">{fluxoLoja.label}</p>
                              <Badge variant="outline" className="text-[10px]">
                                {fluxoLoja.tipo === "consignacao" ? "Consignação" : "Compra"}
                              </Badge>
                            </div>
                            <p className="mt-0.5 text-[11px] text-muted-foreground">{fluxoLoja.proximaAcao}</p>
                            {veiculo.estoque_status && (
                              <p className="mt-1 text-[11px] text-muted-foreground">
                                {ESTOQUE_STATUS_LABELS[(veiculo.estoque_status as EstoqueStatus) || "avaliado"] ?? "Avaliado"}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                          {veiculo.combustivel && (
                            <span className="bg-muted px-2 py-1 rounded">{veiculo.combustivel}</span>
                          )}
                          {veiculo.cambio && (
                            <span className="bg-muted px-2 py-1 rounded">{veiculo.cambio}</span>
                          )}
                          {veiculo.quilometragem !== null && (
                            <span className="bg-muted px-2 py-1 rounded">{veiculo.quilometragem.toLocaleString()} km</span>
                          )}
                        </div>

                        <div className="grid gap-2">
                          <Button size="sm" className="w-full" asChild>
                            <Link to={primaryAction.href}>
                              <PrimaryIcon className="h-4 w-4 mr-2" />
                              {primaryAction.label}
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <Link to={`/veiculo/${veiculo.id}`}>
                              <Eye className="h-4 w-4 mr-2" />
                              Ver detalhes
                            </Link>
                          </Button>
                          {vertical.tipo === "oficina" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full text-muted-foreground"
                              onClick={() => {
                                if (podeDiagnosticoVinculado) {
                                  navigate(`/diagnostico/novo?veiculo=${veiculo.id}&avaliacao=${fluxo!.avaliacao!.id}`)
                                } else {
                                  setDiagnosticoAvulsoVeiculo(veiculo)
                                }
                              }}
                            >
                              <Stethoscope className="h-4 w-4 mr-2" />
                              Novo diagnóstico
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={!!diagnosticoAvulsoVeiculo} onOpenChange={(open) => !open && setDiagnosticoAvulsoVeiculo(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Diagnóstico sem entrada</AlertDialogTitle>
            <AlertDialogDescription>
              Este diagnóstico ainda não está vinculado a uma entrada. Você pode iniciar a entrada primeiro ou criar um diagnóstico avulso.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => {
                if (!diagnosticoAvulsoVeiculo) return
                navigate(`/veiculo/${diagnosticoAvulsoVeiculo.id}`)
              }}
            >
              Iniciar entrada
            </Button>
            <AlertDialogAction
              onClick={() => {
                if (!diagnosticoAvulsoVeiculo) return
                navigate(`/diagnostico/novo?veiculo=${diagnosticoAvulsoVeiculo.id}`)
              }}
            >
              Criar diagnóstico mesmo assim
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default Veiculos
